import { useState } from 'react';

import { FormDTO, FormElement } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useQuery } from '@tanstack/react-query';

import { AWAInspectionsList } from '@/awa-pages/[workspaceId]/assets/[id]/inspections/list';
import { SkeletonList } from '@/awa-pages/[workspaceId]/assets/[id]/inspections/skeleton-list';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import { PlusIcon } from '@/components/icons/plus';
import WarningIcon from '@/components/icons/warning';
import InspectionFormDrawer from '@/components/inspections/inspection-form-drawer';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { getAssetFormInspectionsQueryOptions } from '@/queries/inspections';

export function AWAInspectionsListDrawer({
  assetId,
  form,
  open,
  onOpenChange,
}: {
  assetId: string;
  form: FormDTO & { elements: FormElement[] };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [isInspectionsDrawerOpen, setIsInspectionsDrawerOpen] = useState(false);

  const {
    data: assetFormInspections,
    isLoading: assetFormInspectionsLoading,
    isError: assetFormInspectionsError,
  } = useQuery(getAssetFormInspectionsQueryOptions({ assetId, formId: form.id }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={'right'} className="mx-auto h-full w-full sm:max-w-[800px]" hideCloseIcon onInteractOutside={(e) => e.preventDefault()}>
        <SheetHeader className="border-grey-3 m-0 border-b-2 p-0">
          <div className="flex h-[80px] w-full flex-row items-center justify-between gap-2 bg-white px-4 py-[25px]">
            <div className="mx-auto flex h-[30px] w-full flex-row items-center gap-2 p-0">
              <div
                className="cursor-pointer"
                onClick={() => {
                  onOpenChange(false);
                }}>
                <ArrowLeftIcon size={30} />
              </div>
              <div className="text-default-text text-base font-medium">{form?.name}</div>
            </div>
          </div>
        </SheetHeader>
        <div className="bg-grey-1 flex h-[calc(100vh-1rem)] flex-col overflow-y-auto">
          {assetFormInspectionsLoading ? <SkeletonList /> : <AWAInspectionsList inspections={assetFormInspections || []} assetId={assetId} />}
        </div>

        <Dialog open={!assetFormInspectionsLoading && assetFormInspectionsError && !assetFormInspections}>
          <DialogContent className="w-[354px] rounded-md p-0 sm:w-[500px]">
            <VisuallyHidden>
              <DialogTitle>Error</DialogTitle>
            </VisuallyHidden>
            <DialogHeader>
              <DialogDescription className="flex flex-col items-center gap-2 pt-6">
                <WarningIcon size={30} className="text-default-red" />
                <div className="text-default-text text-xl font-medium">Unable to load</div>
                <div className="text-default-text text-sm font-normal">An error has occurred with loading this asset's forms.</div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mb-6 flex items-center sm:flex-col sm:space-x-0">
              <Button
                className="bg-default-blue mt-4 w-fit rounded-md p-2.5 text-sm font-medium"
                onClick={() => {
                  location.reload();
                }}>
                Retry
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="fixed right-4 bottom-4 mb-[65px] h-[0px] overflow-visible text-right sm:w-[800px]">
          <Button
            variant="default"
            size="icon"
            className={cn(`bg-default-green hover:bg-default-dark-green h-[65px] w-[65px] rounded-full`)}
            onClick={() => {
              setIsInspectionsDrawerOpen(true);
            }}>
            <PlusIcon size={30} />
          </Button>
        </div>
      </SheetContent>

      <InspectionFormDrawer formId={form.id} assetId={assetId} open={isInspectionsDrawerOpen} onOpenChange={setIsInspectionsDrawerOpen} />
    </Sheet>
  );
}
